import React from 'react';

import { UserLink } from '@/components/entity-links';
import { cn } from '@/lib/utils';
import {
  AgentType,
  CustomUserSchema,
} from '@/services/api/generated/matterEventsService';

import { DomainEvent } from '../types';

// Copied from EventComponents to avoid cycle
// Ideally, primitives should be in their own file that doesn't import UserDisplay
const EventEmphasis: React.FC<React.HTMLAttributes<HTMLSpanElement>> = ({
  className,
  children,
  ...props
}) => (
  <span className={cn('font-medium text-gray-900', className)} {...props}>
    {children}
  </span>
);

const formatAgentName = (agent: string) => {
  return agent
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const UserDisplay: React.FC<{
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  fallback?: string;
}> = ({ user, agent, fallback = 'Finch' }) => {
  if (user) {
    const name =
      `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
      user.username;
    return (
      <UserLink
        entityId={user.id}
        className="font-medium text-gray-900 hover:text-blue-600 transition-colors"
      >
        {name}
      </UserLink>
    );
  }

  if (agent) {
    return <EventEmphasis>{formatAgentName(agent)}</EventEmphasis>;
  }

  return <EventEmphasis>{fallback}</EventEmphasis>;
};

export const EventUser: React.FC<{
  event: DomainEvent;
}> = ({ event }) => <UserDisplay user={event.user} agent={event.agent} />;

export const UserChange: React.FC<{
  oldUser?: CustomUserSchema | null;
  newUser?: CustomUserSchema | null;
  action?: 'assigned' | 'unassigned' | 'transferred';
}> = ({ oldUser, newUser, action = 'assigned' }) => {
  if (!newUser) {
    return (
      <>
        <EventEmphasis>unassigned</EventEmphasis>
        {oldUser && (
          <>
            {' from '}
            <UserDisplay user={oldUser} />
          </>
        )}
      </>
    );
  }

  if (!oldUser) {
    return (
      <>
        <EventEmphasis>{action}</EventEmphasis>
        {' to '}
        <UserDisplay user={newUser} />
      </>
    );
  }

  return (
    <>
      <EventEmphasis>transferred</EventEmphasis>
      {' from '}
      <UserDisplay user={oldUser} />
      {' to '}
      <UserDisplay user={newUser} />
    </>
  );
};
